import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import moment from 'moment-timezone';
import _ from 'lodash';
import './style.scss';
import SmallResourcePreview from 'components/Utils/SmallResourcePreview';
import AppointmentNoteTopBar from './AppointmentNoteTopBar';
import {
  AttachmentSidePanel,
  MODES,
  uploadFileToCPP,
} from 'components/HealthRecords/AppointmentNotes/NewAppointmentNote/AppointmentNoteAttachment';
import AppointmentNoteSections, { NOTE_SECTIONS } from './AppointmentNoteSections';
import { applyReasonToStamp } from './stampUtils';
import CPPUtils from 'services/CPPUtils';
import usePersistedState from 'hooks/usePersistedState';
import MonacoTest from 'components/Test/MonacoTest';
import CalendarUtils from 'services/CalendarUtils';
import { useAppSelector } from 'careand-redux/store';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { api } from 'careand-redux/api/ApiThunks';
import BeautifyModal from 'components/HealthRecords/AppointmentNotes/NewAppointmentNote/BeautifyModal';
import {
  BASIC_NOTE,
  METRICS,
  OTHER_HEALTH_RECORDS,
} from 'components/HealthRecords/AppointmentNotes/NewAppointmentNote/constants';
import { ActivePhysician, InternOf, IsTypeNP } from 'careand-redux/selectors';
import TextareaAutosize from 'react-textarea-autosize';
import PerformanceUtils from 'services/PerformanceUtils';
import DefaultButton from 'components/Utils/NewDefaultButton';
import AppointmentUtils from 'services/AppointmentUtils';
import MergeTranscriptionModal from './MergeTranscriptionModal';
import {
  AppointmentNote,
  AppointmentNoteDraft,
  GetLatestMetricValueOriginEnum,
} from 'generated/api';
import PatientUtils from 'services/PatientUtils';
import { GetPhysician } from 'careand-redux/selectors';
import { useCPPContext } from '../../../Patient/CPP/CPPContext';
import { ActionPanel } from 'components/ProactiveCarey/ActionPanel';
import CommonTextareaAutosize from 'components/Common/TextAreaAutosize';
import config from 'config';
import { useReduxForm } from '../../../../hooks/useReduxForm';

let savingDraftLock = false;

export const NOTE_TEXT_MODE = {
  v1: 'v1',
  v2: 'v2 (beta)',
};

const DEFAULT_ARRAY: any[] = [];
const DEFAULT_OBJECT: Record<string, any> = {};
const DEBOUNCE_DELAY = 900;

// ─── Zod Schema ────────────────────────────────────────────────────────────────

const appointmentNoteSchema = z.object({
  metrics: z.any().optional(),
  processedMetrics: z.any().optional(),
  itemId: z.string().optional().nullable(),
  authorId: z.string().optional().nullable(),
  notes: z.string().optional().nullable(),
  item_type: z.string().optional().nullable(),
  date: z.number().optional().nullable(),
  draftId: z.string().optional().nullable(),
  sections_key: z.any().optional().nullable(),
  health_records: z.any().optional(),
  template: z.any().optional().nullable(),
  documents: z.array(z.string()).optional().nullable(),
  beautifyValues: z.any().optional().nullable(),
  forceNote: z.any().optional().nullable(),
  originalNote: z.any().optional().nullable(),
  hide: z.boolean().optional().nullable(),
  value: z.string().optional().nullable(),
});

type AppointmentNoteFormValues = z.infer<typeof appointmentNoteSchema>;

// ─── Props ─────────────────────────────────────────────────────────────────────

interface NewAppointmentNoteProps {
  patient: any;
  onClose?: () => void;
  overrideFormId?: string;
  appointmentId?: string;
  askProactiveCarey?: (params: any) => void;
}

const NewAppointmentNote: React.FC<NewAppointmentNoteProps> = (props) => {
  const { patient, appointmentId, askProactiveCarey } = props;

  const activeUserId = useAppSelector((state) => state.authentication.activeUserId);

  const { unreactiveNoteValue } = useCPPContext();
  const saveButtonRef = useRef<HTMLButtonElement | null>(null);

  const formName = props.overrideFormId || CPPUtils.getFormName('note', patient.id);

  // ─── React Hook Form ──────────────────────────────────────────────────────

  const { control, watch, setValue, reset, getValues, handleSubmit } =
    useReduxForm<AppointmentNoteFormValues>(formName, {
      transient: true,
      destroyOnUnmount: true,
      resolver: zodResolver(appointmentNoteSchema),
      defaultValues: {
        metrics: [],
        processedMetrics: undefined,
        itemId: undefined,
        authorId: undefined,
        notes: undefined,
        item_type: undefined,
        date: moment().unix(),
        draftId: undefined,
        sections_key: undefined,
        health_records: DEFAULT_OBJECT,
        template: undefined,
        documents: undefined,
        beautifyValues: undefined,
        forceNote: undefined,
        originalNote: undefined,
        hide: undefined,
        value: undefined,
      },
    });

  // Watch form values that were previously from formValueSelector
  const itemId = watch('itemId');
  const metrics = watch('metrics');
  const processedMetrics = watch('processedMetrics');
  const health_records = watch('health_records') || DEFAULT_OBJECT;
  const documents = watch('documents');
  const item_type = watch('item_type');
  const date = watch('date');
  const draftId = watch('draftId');
  const sections_key = watch('sections_key');
  const beautifyValues = watch('beautifyValues');
  const originalNoteFormValue = watch('originalNote');
  const hideValue = watch('hide');

  const valueRef = useRef(getValues().originalNote?.value?.value || '');

  const stamps = useAppSelector((state) => state.healthRecords.appointmentNoteStamps);
  const defaultStampId = useAppSelector(
    (state) => GetPhysician(state)?.preferences?.default_soap_stamp,
  );

  const defaultStamp = useMemo(() => {
    let res: any = null;

    if (defaultStampId) {
      res = stamps?.find(({ id }: any) => id === defaultStampId);
    }

    return res || { content: '' };
  }, [stamps, defaultStampId]);

  const dispatch = useAppDispatch();

  const patientId = patient.id;

  const templatesSelector = useAppSelector((state) => state.templates);
  const patientHr =
    useAppSelector((state) => state.healthRecords.byId[patient.id]) || DEFAULT_OBJECT;

  const templates = templatesSelector.templates;
  const drafts = patientHr.appointmentNoteDrafts;
  const patientSummary = patientHr.dataSummary || DEFAULT_ARRAY;

  const physician = useAppSelector(ActivePhysician);
  const isIntern = !!useAppSelector(InternOf);
  const isTypeNP = useAppSelector(IsTypeNP);

  const [loading, setLoading] = useState<string | boolean>(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [draft, setDraft] = useState<any>();
  // Auto-saves fire on every keystroke while the draft is out of sync, so when
  // "Save Note" is clicked a draft save can still be in flight and the `draft`
  // captured in a render closure can be stale (or undefined for the first
  // save). The delete-on-save must target the draft that actually exists on
  // the server, and no auto-save may run once the note save has started —
  // otherwise the deleted draft gets re-created and shows up as a duplicate.
  const draftRef = useRef<any>();
  const draftSavePromiseRef = useRef<Promise<any> | null>(null);
  const noteSaveInProgressRef = useRef(false);

  function updateDraft(newDraft: any) {
    draftRef.current = newDraft;
    setDraft(newDraft);
  }
  const [originalNote, setOriginalNote] = useState<any>(() =>
    prepareData(valueRef.current, getValues(), itemId, patientId),
  );
  const [draftMatches, setDraftMatches] = useState(true);
  const [showMergeTranscription, setShowMergeTranscription] = useState(false);
  const [filledMetrics, setFilledMetrics] = useState(false);
  const [noteVersion, setNoteVersion] = usePersistedState('noteVersion2', NOTE_TEXT_MODE.v1);
  const [currentMode, setCurrentMode] = useState<string>();
  const [showBeautify, setShowBeautify] = useState(false);
  const [selectNote, setSelectNote] = useState(!draftId && !itemId);
  const [canSaveDraft, setCanSaveDraft] = useState(false);
  const [changeKey, setChangeKey] = useState(1);

  const appointments = useAppSelector((state) => state.appointments.activeAppointments);
  const permissions = useAppSelector((state) => state.authentication.permissions);
  const patientAppointments = useAppSelector((state) => state.appointments.byId[patientId]);

  useEffect(() => {
    if (hideValue === undefined && !isTypeNP) {
      setValue('hide', true);
    }
  }, [isTypeNP, hideValue, selectNote]); // eslint-disable-line react-hooks/exhaustive-deps

  const ownId = useAppSelector((state) => state.authentication.activeUserId!);
  const ownUser = useAppSelector((state) => state.physicians.byId[ownId]);
  const ownDrafts = useMemo(() => {
    return _.reverse(_.sortBy(drafts?.filter((d: any) => d.author_id === ownId), ['date']));
  }, [ownId, drafts]);
  const draftsLength = ownDrafts.length;

  const appointment = appointments[appointmentId as string];

  const appointmentWithTranscription =
    appointment ||
    (ownUser
      ? CPPUtils.getTranscriptionAppointment(patientAppointments, ownUser, permissions)
      : undefined);

  const draftsLoaded = drafts !== undefined;
  useEffect(() => {
    if (draftsLoaded && draftsLength === 0) {
      setSelectNote(false);
      // Only clear form if we're not already editing (i.e., still on draft selection
      // or haven't loaded any draft data yet). Without this guard, a health records
      // refresh that temporarily empties ownDrafts would wipe the note with the stamp.
      if (!draft && !itemId) {
        clearFormCallback();
      }
    }
  }, [draftsLoaded, draftsLength]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (draftId) {
      // Prefer the actual draft from the server-loaded drafts list
      let foundDraft = drafts?.find((d: any) => d.id === draftId);
      if (foundDraft) {
        return onSelectNote(foundDraft);
      }

      // Fall back to locally-constructed draft from originalNote state
      if (originalNote) {
        let newDraft = {
          id: draftId,
          patient_id: originalNote.patient_id,
          note: originalNote,
          author_id: ownId,
        };
        return onSelectNote(newDraft);
      }
    }
  }, [drafts, draftId, ownId, originalNote]); // eslint-disable-line react-hooks/exhaustive-deps

  const reason = appointment?.complaints;

  const noteRef = useRef<HTMLTextAreaElement | null>(null);

  function insertText(
    textToInsert: string,
    insertAtEnd = false,
    replaceText = false,
    openExtractHR = false,
  ) {
    if (replaceText) {
      onChangeWrapper('value', textToInsert);
    } else {
      let text = valueRef.current || '';

      let noteCursorPosition = 0;

      if (noteRef && noteRef.current) noteCursorPosition = noteRef.current.selectionStart;

      if (insertAtEnd) noteCursorPosition = text.length;

      let result =
        text.slice(0, noteCursorPosition) + textToInsert + text.slice(noteCursorPosition);

      onChangeWrapper('value', result);
    }
    if (openExtractHR) {
      setShowBeautify(true);
    }
  }

  const filledWeight = !!processedMetrics?.weight;

  const prefillMetrics = useMemo(() => {
    const keys = ['weight', 'height'];
    let result: Record<string, any> = {};
    keys.forEach((key) => {
      let data = patientSummary.find(
        (val: any) =>
          val.metric.id === key && val.origin === GetLatestMetricValueOriginEnum.Practitioner,
      );
      if (data) {
        const dataDate = data?.last_value?.date;
        const isSameDay = CalendarUtils.isToday(moment.unix(dataDate));
        if (isSameDay) {
          result[key] = data?.last_value?.value1;
        } else {
          const over23 = PatientUtils.getAgeAsNumber(patient)! >= 23;
          const hasFilledWeight = result.weight || filledWeight;
          if (over23 && data.metric.id === 'height' && hasFilledWeight) {
            result[key] = data?.last_value?.value1;
          }
        }
      }
    });
    if (Object.keys(result).length > 0) return result;
  }, [patientSummary, patient, filledWeight]);

  useEffect(() => {
    if (!processedMetrics) return;
    if (!!prefillMetrics && !filledMetrics) {
      let toChange: Record<string, any> = {};
      Object.keys(prefillMetrics).forEach((key) => {
        if (!(processedMetrics || {})[key]) {
          toChange[key] = prefillMetrics[key];
        }
      });

      if (Object.keys(toChange).length > 0) {
        setFilledMetrics(true);
        setTimeout(() => {
          setValue('processedMetrics', {
            ...(processedMetrics || {}),
            ...toChange,
          });
        }, 500);
      }
    }
  }, [prefillMetrics, filledMetrics, processedMetrics]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (processedMetrics) {
      let { height, weight, bmi } = processedMetrics;
      let newBMI;
      if (height && weight) {
        newBMI = CPPUtils.calculateBMI(height, weight);
      } else {
        newBMI = undefined;
      }
      if (bmi !== newBMI) {
        let newProcessedMetrics = {
          ...processedMetrics,
          bmi: newBMI,
        };
        setValue('processedMetrics', newProcessedMetrics);
      }
    }
  }, [processedMetrics]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (beautifyValues) {
      let { note, records, save } = beautifyValues;
      onAcceptBeautifyChanges(note, records, save);
      setValue('beautifyValues', null);
    }
  }, [beautifyValues]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(
    () => {
      if (Object.keys(processedMetrics || {}).length === 0) {
        let result: Record<string, any> = {};
        (metrics || []).forEach((metric: any) => {
          // If this metric name already exists, convert to array
          if (result[metric.name]) {
            if (Array.isArray(result[metric.name])) {
              result[metric.name].push(metric.value);
            } else {
              result[metric.name] = [result[metric.name], metric.value];
            }
          } else {
            result[metric.name] = metric.value;
          }
        });
        setValue('processedMetrics', result);
      }
    },
    [metrics], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const templatesFetchedRef = useRef(false);
  useEffect(() => {
    if (!templatesFetchedRef.current && !templates?.length) {
      templatesFetchedRef.current = true;
      dispatch(api.Templates.getDocumentTemplates());
    }
  }, [dispatch, templates]);

  function changeValueWithDebouce(val: string, forceTextAreaValue = false, checkDraft = true) {
    valueRef.current = val;
    unreactiveNoteValue.current = val;

    if (forceTextAreaValue && noteRef.current) {
      noteRef.current.value = val;
    }

    if (checkDraft) {
      setChangeKey((prevState) => prevState + 1);
      setCanSaveDraft(true);
      debouncedCheckDraftMatches(
        draft,
        health_records,
        originalNote,
        processedMetrics,
        documents,
        val,
      );
    }
  }

  function onAcceptBeautifyChanges(newNote: string | null, newRecords: any, save = false) {
    setShowBeautify(false);
    if (!save) {
      setCanSaveDraft(false);
    }

    if (newNote) {
      changeValueWithDebouce(newNote, true, !save);
    }
    if (newRecords) {
      Object.entries(newRecords).forEach(([id, value]: [string, any]) => {
        if (id === 'metrics') {
          // Group metrics by name to handle multiple values
          const groupedMetrics: Record<string, any> = {};
          value?.forEach((entry: any) => {
            if (entry.value != null && entry.value !== '') {
              if (groupedMetrics[entry.name]) {
                if (Array.isArray(groupedMetrics[entry.name])) {
                  groupedMetrics[entry.name].push(entry.value);
                } else {
                  groupedMetrics[entry.name] = [groupedMetrics[entry.name], entry.value];
                }
              } else {
                groupedMetrics[entry.name] = entry.value;
              }
            }
          });
          // Apply the grouped metrics
          const current = getValues('processedMetrics') || {};
          const updated = { ...current };
          Object.entries(groupedMetrics).forEach(([name, metricValue]) => {
            updated[name] = metricValue;
          });
          setValue('processedMetrics', updated);
        } else if (NOTE_SECTIONS.includes(id)) {
          document.dispatchEvent(
            new CustomEvent('appointmentNoteStackUpdated', {
              detail: { id, stack: value },
            }),
          );
        }
      });
    }

    if (save) {
      setTimeout(() => {
        setCanSaveDraft(false);
        saveButtonRef.current?.click();
      }, DEBOUNCE_DELAY);
    }
  }

  function checkDraftMatches(
    draftArg: any,
    healthRecordsArg: any,
    originalNoteArg: any,
    processedMetricsArg: any,
    documentsArg: any,
    value: string,
  ) {
    let comparisonDraft = draftArg;

    if (!comparisonDraft) {
      if (!originalNoteArg) return true;
      comparisonDraft = {
        note: originalNoteArg,
      };
    }

    if (!comparisonDraft.note) return;

    let noteEqual = value === comparisonDraft.note.value.value;
    let currentMetrics = prepareMetrics(processedMetricsArg, true);
    let metricsEqual = _.isEqual(currentMetrics, comparisonDraft.note.value.metrics);
    let documentsEqual = _.isEqual(documentsArg, comparisonDraft.note.documents);
    let currentHealthRecords = prepareHealthRecords(healthRecordsArg);
    let healthRecordsEqual = _.isEqual(currentHealthRecords, comparisonDraft.note.health_records);

    let matches = noteEqual && metricsEqual && healthRecordsEqual && documentsEqual;
    setDraftMatches(matches);
    if (matches) setCanSaveDraft(false);
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedCheckDraftMatches = useCallback(_.debounce(checkDraftMatches, DEBOUNCE_DELAY), []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSetDraftMatches = useCallback(_.debounce(setDraftMatches, DEBOUNCE_DELAY), []);

  useEffect(() => {
    setCanSaveDraft(true);
  }, [health_records, originalNote, processedMetrics]);

  useEffect(() => {
    debouncedCheckDraftMatches(
      draft,
      health_records,
      originalNote,
      processedMetrics,
      documents,
      valueRef.current,
    );
  }, [
    draft,
    health_records,
    originalNote,
    processedMetrics,
    documents,
    debouncedCheckDraftMatches,
  ]);

  useEffect(
    () => {
      let draftTimeout: ReturnType<typeof setTimeout>;

      function configureDraftInterval(onlyClear = false) {
        clearTimeout(draftTimeout);
        if (onlyClear) return;
        if (draftMatches) return;
        handleSubmit((values) => saveDraft(valueRef.current, values))();
        draftTimeout = setTimeout(
          () => handleSubmit((values) => saveDraft(valueRef.current, values))(),
          0,
        );
      }

      configureDraftInterval();
      return () => {
        configureDraftInterval(true);
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [draftMatches, draft, documents, health_records, processedMetrics, changeKey],
  );

  async function saveDraft(value: string, values: AppointmentNoteFormValues) {
    if (savingDraftLock || !canSaveDraft || noteSaveInProgressRef.current) return;
    savingDraftLock = true;

    try {
      let note = prepareData(value, values, itemId, patientId);

      if (!note.value) {
        return;
      }

      let currentDraft = draftRef.current || {};

      setSavingDraft(true);
      let newDraft = {
        id: currentDraft.id || note.id,
        patient_id: note.patient_id,
        note,
        author_id: currentDraft.author_id,
      };

      let success = true;

      if (note.value?.value) {
        const savePromise = dispatch(
          api.HealthRecords.addNotesDraft({
            appointmentNoteDraft: newDraft as AppointmentNoteDraft,
          }),
          {
            errorTitle: 'Note Draft',
          },
        );
        draftSavePromiseRef.current = savePromise;
        let result = await savePromise;
        if (result) {
          updateDraft(result);
        } else {
          success = false;
        }
      }
      if (success) {
        setDraftMatches(true);
        setCanSaveDraft(false);
      }
    } finally {
      setSavingDraft(false);
      savingDraftLock = false;
    }
  }

  function onSelectNote(draftArg: any) {
    clearFormCallback();
    changeValueWithDebouce(draftArg.note.value.value, true);
    setValue('date', draftArg.note.value.date);
    updateDraft(draftArg);
    setSelectNote(false);
    if (draftArg.note) {
      let data = CPPUtils.getAppointmentNoteEditElement(draftArg.note, draftArg.note.id);
      // Replace MiscUtils.changeForm: reset the form with the extracted data
      reset({
        ...getValues(),
        ...data,
        originalNote: null,
      });
    }
    setValue('draftId', null);
  }

  async function makeChanges(value: string, values: AppointmentNoteFormValues, close = true) {
    let note = prepareData(value, values, itemId, patientId);
    setLoading(close ? 'save-close' : 'save');
    noteSaveInProgressRef.current = true;

    note.item_type = 'text';

    const options = {
      errorTitle: 'Encounter Note',
    };

    let result;
    if (itemId)
      result = await dispatch(
        api.HealthRecords.editNote({ id: itemId, appointmentNote: note as AppointmentNote }),
        options,
      );
    else
      result = await dispatch(
        api.HealthRecords.addNotesText({ appointmentNote: note as AppointmentNote }),
        options,
      );

    setLoading(false);

    if (result) {
      if (close) {
        setCanSaveDraft(false);
      }
      // An auto-save dispatched before the click may still be in flight; let it
      // settle so the delete below targets the draft that exists server-side
      // and cannot be outrun by it.
      if (draftSavePromiseRef.current) {
        try {
          await draftSavePromiseRef.current;
        } catch (e) {
          // saveDraft surfaces its own errors
        }
      }
      if (draftRef.current) {
        dispatch(
          api.HealthRecords.deleteNotesDraft({
            patientId: note.patient_id,
            id: draftRef.current.id,
          }),
        ).then(() => {
          dispatch(api.HealthRecords.getHealthRecords({ patientId: note.patient_id }));
        });
      }
      if (close) {
        clearFormCallback();
        if (props.onClose) props.onClose();
      } else {
        noteSaveInProgressRef.current = false;
        setValue('itemId', result.id);
      }
    } else {
      noteSaveInProgressRef.current = false;
    }
  }

  const clearFormCallback = useCallback(
    (value: string = defaultStamp?.content) => {
      const stampValue = applyReasonToStamp(value, reason);
      reset();
      const data = prepareData(stampValue, getValues(), undefined, patientId);
      setOriginalNote(data);
      setFilledMetrics(false);
      updateDraft(undefined);
      setValue('date', moment().unix());
      changeValueWithDebouce(stampValue, true);
      setSelectNote(false);
      setCanSaveDraft(false);
    },
    [reason, patientId, defaultStamp, reset, getValues], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const onChangeWrapper = useCallback((key: string, value: any) => {
    if (key === 'value') changeValueWithDebouce(value, true);
    else if (key.startsWith('processedMetrics.')) {
      const metricKey = key.replace('processedMetrics.', '');
      const current = getValues('processedMetrics') || {};
      setValue('processedMetrics', { ...current, [metricKey]: value });
    } else if (key.startsWith('health_records.')) {
      const hrKey = key.replace('health_records.', '');
      const current = getValues('health_records') || {};
      setValue('health_records', { ...current, [hrKey]: value });
    } else setValue(key as any, value);
    debouncedSetDraftMatches(false);
    setCanSaveDraft(true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  let noteWrapperClassName = 'row note-wrapper';
  if (
    currentMode === MODES.addAttachment ||
    currentMode === MODES.addStamp ||
    currentMode === MODES.previewAttachment
  )
    noteWrapperClassName += ' attachments-open';

  const onDragDocumentToField = (e: React.DragEvent) => {
    e.preventDefault();
    let files: File[] = [];
    if (e.dataTransfer.items) {
      files = [...e.dataTransfer.items]
        .filter((it) => it.kind === 'file')
        .map((it) => it.getAsFile()!);
    } else {
      files = [...e.dataTransfer.files];
    }
    files.map((file) =>
      uploadFileToCPP(file, {
        dispatch,
        setUploading: (l: boolean) => console.log('loading: ', l),
        isIntern,
        patientId,
        onSuccess: (document: any) => {
          const newDocuments = [...(documents || []), document.id];
          onChangeWrapper('documents', newDocuments);
        },
      }),
    );
  };

  const clearFormBasic = useCallback(() => {
    clearFormCallback(BASIC_NOTE);
  }, [clearFormCallback]);

  const noteType = draft?.note?.id ? 'an existing note' : 'a new note';

  useEffect(() => {
    if (noteRef.current) {
      noteRef.current.value = valueRef.current;
    }
  }, [selectNote]);

  const showTranscriptionButton = AppointmentUtils.isTranscriptionAvailable(
    appointmentWithTranscription,
    ownUser,
    permissions,
  );

  return (
    <div
      className="cpp-input new-appointment-note"
      style={{ display: 'flex', height: '100%', flexDirection: 'column' }}
    >
      <AppointmentNoteTopBar
        control={control}
        onClose={props.onClose}
        onClear={clearFormBasic}
        onStartNewNote={() => clearFormCallback()}
        date={date}
        onChange={onChangeWrapper}
        physician={physician}
        savingDraft={savingDraft}
        canSaveDraft={canSaveDraft && !selectNote}
        loading={loading}
        onSave={() => handleSubmit((values) => makeChanges(valueRef.current, values, true))()}
        saveButtonRef={saveButtonRef}
        selectNote={selectNote}
        extraButtons={
          <>
            {showTranscriptionButton && (
              <DefaultButton
                className="green"
                style={{ marginLeft: '10px', width: '235px', height: '35px' }}
                name="Complete from Transcription"
                icon="sparkles"
                onClick={() => setShowMergeTranscription(true)}
              />
            )}
          </>
        }
      />
      <div className="flex overflow-hidden">
        <div className="overflow-auto no-scrollbar grow">
          <div
            style={{
              width: '300px',
              position: 'absolute',
              display: 'flex',
              flexDirection: 'row',
              left: '150px',
              top: '5px',
              zIndex: 1,
            }}
          >
            {showMergeTranscription && (
              <MergeTranscriptionModal
                onClose={() => setShowMergeTranscription(false)}
                appointment={appointmentWithTranscription!}
                note={valueRef.current}
                insertText={insertText}
                patient={patient}
              />
            )}
            {showBeautify && (
              <BeautifyModal
                open={showBeautify}
                onClose={() => setShowBeautify(false)}
                patient={patient}
                reason={reason}
                patientHr={patientHr}
                value={valueRef.current}
                onAccept={onAcceptBeautifyChanges}
                includeNote={false}
                title="Extract Health Records"
              />
            )}
          </div>
          {!selectNote ? (
            <div className="data-content">
              <div className="row">
                {item_type === 'attachment' && (
                  <>
                    <SmallResourcePreview
                      url={valueRef.current || ''}
                      getFile={(dispatchOptions: any) =>
                        dispatch(
                          api.HealthRecords.getNotesAttachment({
                            appointmentNoteId: itemId!,
                          }),
                          dispatchOptions,
                        )
                      }
                    />
                    <CommonTextareaAutosize name="notes" control={control} cacheMeasurements />
                  </>
                )}
                <span className="explanation">
                  You are editing <b>{noteType}</b>
                </span>
                <div className="main-note-wrapper">
                  <div className={noteWrapperClassName}>
                    {noteVersion === NOTE_TEXT_MODE.v2 && (
                      <MonacoTest
                        id="text-value-v2"
                        value={valueRef.current}
                        onChange={(text: string) => {
                          setCanSaveDraft(true);
                          changeValueWithDebouce(text);
                        }}
                        metrics={patientSummary}
                      />
                    )}
                    {noteVersion === NOTE_TEXT_MODE.v1 && (
                      <TextareaAutosize
                        ref={(ref: HTMLTextAreaElement | null) => {
                          if (ref) {
                            ref.value = valueRef.current;
                          }
                        }}
                        // @ts-expect-error - TextareaAutosize forwardRef prop
                        forwardRef={noteRef}
                        id="text-value"
                        name="value"
                        minRows={15}
                        autoFocus={true}
                        onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
                          noteRef.current = event.target;
                          changeValueWithDebouce(event.target.value);
                        }}
                        onDrop={onDragDocumentToField}
                      />
                    )}
                    <AttachmentSidePanel
                      key={itemId ?? undefined}
                      noteId={itemId}
                      patient={patient}
                      form={formName}
                      valueRef={valueRef}
                      documents={documents}
                      onChange={onChangeWrapper}
                      currentMode={currentMode}
                      setCurrentMode={setCurrentMode}
                      showBeautify={setShowBeautify}
                      reason={reason}
                      draftId={draftId || draft?.id}
                      onClose={props.onClose}
                      appointmentId={appointmentId}
                      insertText={insertText}
                    />
                  </div>
                </div>

                <AppointmentNoteSections
                  key={itemId ?? undefined}
                  sections_key={sections_key}
                  health_records={health_records}
                  patient={patient}
                  onChange={onChangeWrapper}
                  valueRef={valueRef}
                  patientHr={patientHr}
                  processedMetrics={processedMetrics}
                />
              </div>
            </div>
          ) : (
            <SelectNote
              drafts={ownDrafts}
              onSelect={onSelectNote}
              draftId={draftId}
              originalNote={originalNoteFormValue}
            />
          )}
        </div>
        {(config.ENVIRONMENT === 'staging' ||
          activeUserId === '53a3af20-0842-11e8-b499-2d06ff911a34' ||
          activeUserId === '8063e17a-71e8-43b9-8181-414fa5d9a6e3' ||
          activeUserId === '2f564813-3c61-49c3-96e4-71761795a65e') &&
          appointmentId &&
          !selectNote && (
            <ActionPanel appointmentId={appointmentId} getSuggestions={askProactiveCarey!} />
          )}
      </div>
    </div>
  );
};

interface SelectNoteProps {
  drafts: any[];
  onSelect: (draft: any) => void;
  draftId?: string | null;
  originalNote?: any;
}

const SelectNote: React.FC<SelectNoteProps> = ({ drafts, onSelect }) => {
  return (
    <div className="select-note">
      <span className="small-title">Select an existing draft or start a new one</span>
      <div className="drafts">
        {drafts.map((draftItem: any) => {
          return (
            <div className="draft" key={draftItem.id} onClick={() => onSelect(draftItem)}>
              <span className="draft-date">
                {CalendarUtils.format(draftItem.date, 'DD/MM/YYYY h:mm A')}
              </span>
              <pre>{draftItem.note.value.value}</pre>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default NewAppointmentNote;

// ─── Helper Functions ──────────────────────────────────────────────────────────

function prepareData(
  value: string,
  values: Record<string, any>,
  itemIdArg: string | undefined | null,
  patientId: string,
) {
  values = PerformanceUtils.clone(values);

  let metricsResult = prepareMetrics(values.processedMetrics);
  let healthRecordsResult = prepareHealthRecords(values.health_records);

  return {
    id: itemIdArg,
    patient_id: patientId,
    item_type: values.item_type || 'text',
    value: {
      value: value || '',
      date: values.date || undefined,
      hide: values.hide,
      metrics: metricsResult,
      notes: values.notes,
    },
    documents: values.documents,
    health_records: healthRecordsResult,
  };
}

function prepareHealthRecords(healthRecords: Record<string, any> = {}) {
  let result: Record<string, any> = {};
  OTHER_HEALTH_RECORDS.forEach((hr: any) => {
    if (hr.id === 'gtpal') {
      result[`${hr.id}_main`] = {
        ...(healthRecords[`${hr.id}_main`] || {}),
      };
    }
    result[hr.id] = healthRecords[hr.id] || [];
  });
  return result;
}

function prepareMetrics(metricValues: Record<string, any> = {}, skipDelete = false) {
  let metricsResult: Array<{ name: string; value: any }> = [];

  METRICS.forEach((metric: any) => {
    let key = metric.metric;
    const value = metricValues[key];

    if (value) {
      // Handle both single values and arrays
      if (Array.isArray(value)) {
        // Add each value in the array as a separate metric entry
        value.forEach((val: any) => {
          if (val && val.trim && val.trim() !== '') {
            metricsResult.push({
              name: metric.metric,
              value: val,
            });
          }
        });
      } else {
        // Single value
        metricsResult.push({
          name: metric.metric,
          value: value,
        });
      }
    }
    if (!skipDelete) delete metricValues[key];
  });

  return metricsResult;
}
